import {
  HierarchyNode,
  HierarchyRectangularNode,
  ScaleOrdinal,
  color,
  hierarchy,
  interpolateRgb,
  scaleOrdinal,
  schemeDark2,
  select,
  treemap,
  treemapSquarify
} from 'd3';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

const FONT_SIZE = 12;

export type TreeMapData = {
  name: string;
  category?: string;
  value?: number;
  children?: TreeMapData[];
  colorScheme?: string[];
};

const TreeMap: FC<{
  data: TreeMapData;
  height: number;
  width: number;
  total?: number;
  focused?: boolean;
  colorScheme?: string[];
  customFilter?: (node: TreeMapData) => boolean;
  customSort?: (a: HierarchyNode<TreeMapData>, b: HierarchyNode<TreeMapData>) => number;
  handleClick?: (...args: [PointerEvent, HierarchyNode<TreeMapData>]) => any;
}> = ({ data, height, width, total, focused, colorScheme = schemeDark2, customFilter, customSort, handleClick }) => {
  const svgRef = useRef(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [hovered, setHovered] = useState<[MouseEvent, HierarchyNode<TreeMapData>]>();

  const renderTreemap = useCallback(() => {
    const svg = select(svgRef.current);
    svg.selectAll('g').remove();
    svg.selectAll('div').remove();

    svg.attr('width', width).attr('height', height);

    /* if total count provided, limit smallest rects when unfocused for readability */
    // const minValue = total / (hierarchy(data).count().value ?? 0) / 4;
    const minValue = 0.01 * (total ?? 0);

    const root = hierarchy<TreeMapData>(data)
      .sum(d => d.value ?? 0)
      .sort((a, b) => customSort?.(a, b) || (b.value ?? 0) - (a.value ?? 0))
      .sum(d => (d.value && (!customFilter || customFilter(d)) ? Math.max(Number(!focused) * minValue, d.value) : 0));

    const treemapRoot = treemap<TreeMapData>().size([width, height]).padding(1).tile(treemapSquarify.ratio(1))(root);
    const defaultColorScale = scaleOrdinal(colorScheme.map(color => interpolateRgb(color, '#fff')(0.3)));

    type ColorScale = { [key: string]: ScaleOrdinal<string, string> };
    const colorReducer: any = (acc: ColorScale, { data }: HierarchyNode<TreeMapData>) => {
      if (!data.children) {
        return acc;
      } else if (!data.colorScheme) {
        return { ...acc, ...hierarchy(data).children?.reduce(colorReducer, {}) };
      } else
        return {
          ...acc,
          [data.name]: scaleOrdinal(data.children.map(({ name }) => name)).range(data.colorScheme)
        };
    };
    const colorScales: ColorScale = (treemapRoot.children ?? []).reduce(colorReducer, {});

    const nodes = svg
      .selectAll('g')
      .data(treemapRoot.leaves())
      .join('g')
      .attr('transform', d => `translate(${d.x0},${d.y0})`);

    const getColor = (d: HierarchyRectangularNode<TreeMapData>) =>
      d.data.category! in colorScales
        ? colorScales[d.data.category!](d.data.name)
        : defaultColorScale(d.data.category!);

    const getTextColor = (d: HierarchyRectangularNode<TreeMapData>) => {
      const background = color(getColor(d));
      if (background) {
        const RsRGB = background.rgb().r / 255;
        const GsRGB = background.rgb().g / 255;
        const BsRGB = background.rgb().b / 255;
        const R = RsRGB <= 0.03928 ? RsRGB / 12.92 : Math.pow((RsRGB + 0.055) / 1.055, 2.4);
        const G = GsRGB <= 0.03928 ? GsRGB / 12.92 : Math.pow((GsRGB + 0.055) / 1.055, 2.4);
        const B = BsRGB <= 0.03928 ? BsRGB / 12.92 : Math.pow((BsRGB + 0.055) / 1.055, 2.4);
        const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
        if (luminance > Math.sqrt(1.05 * 0.5)) return '#000';
      }
      return '#fff';
      // return background && hsl(background).l > 0.5 ? '#000' : '#fff';
    };

    nodes
      .append('rect')
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .attr('fill', getColor)
      .style('cursor', 'pointer')
      .on('mousemove', (...d) => setHovered(d))
      .on('mouseleave', () => setHovered(undefined))
      .on('click', handleClick as any);

    const visited = new Set();

    // add text to rects
    nodes
      .filter(n => visited.size !== visited.add(n.data.name).size)
      .append('text')
      .text(d => {
        let label = `${d.data.name?.toUpperCase()} (${d.data.value})`;
        if ((d.data.value ?? 0) > minValue) return label;
        return label.split(/\s/)[0].slice(0, 4);
      })
      .attr('data-width', d => d.x1 - d.x0)
      .attr('font-size', `${FONT_SIZE}px`)
      .attr('font-weight', `bold`)
      .attr('fill', getTextColor)
      .attr('x', 3)
      .attr('y', FONT_SIZE)
      .style('pointer-events', 'none');
  }, [data, height, width, total, focused, colorScheme, customFilter, customSort, handleClick]);

  const renderTooltip = useCallback(() => {
    const tooltip = select(tooltipRef.current);
    const size = tooltip.node()?.getBoundingClientRect();
    if (hovered?.[0]?.target instanceof SVGRectElement) {
      const x = Math.max(0, Math.min(width - (size?.width ?? 0), hovered[0].offsetX));
      const y = Math.max(0 + (size?.height ?? 0), Math.min(height, hovered[0].offsetY));
      tooltip
        .style('transform', `translate(${x}px,calc(${y}px - 100%))`)
        .style('opacity', 1)
        .html(
          (!hovered[1].parent?.data.category ? '' : `${hovered[1].parent.data.category}<br/>`) +
            `${hovered[1].data.category}<br/>` +
            `${hovered[1].data.category !== hovered[1].data.name ? hovered[1].data.name : ''}<br/>` +
            `Count: ${hovered[1].data.value}`
        );
    } else {
      tooltip.style('opacity', 0);
    }
  }, [hovered, height, width]);

  useEffect(renderTreemap, [renderTreemap]);
  useEffect(renderTooltip, [renderTooltip]);

  return (
    <div className="h-100">
      <div className="tooltip p-3" ref={tooltipRef} />
      {!total ? <p className="no-transactions">NO DATA</p> : <svg ref={svgRef} />}
    </div>
  );
};

export default TreeMap;
